import React, { useEffect, useMemo, useState } from "react";
import useReveal from "../hooks/useReveal";
import {
  Tag,
  Calendar,
  ArrowUpRight,
  Snowflake,
  Disc,
  Wrench,
  Droplets,
  ClipboardCheck,
  Sparkles,
} from "lucide-react";

const OFFERS = [
  {
    id: "oel",
    title: "Ölwechsel",
    discount: "10%",
    desc: "10 % Rabatt auf den Ölwechsel — Markenöl, neuer Filter, Service-Dokumentation inklusive.",
    icon: Droplets,
  },
  {
    id: "klima",
    title: "Klimaservice",
    discount: "10%",
    desc: "Frische Luft im Innenraum: Desinfektion, Dichtigkeitsprüfung und Auffüllen — jetzt 10 % günstiger.",
    icon: Snowflake,
    seasonal: true,
  },
  {
    id: "bremsen",
    title: "Bremsen",
    discount: "10%",
    desc: "Bremsbeläge, Bremsscheiben oder die komplette Anlage — 10 % Rabatt auf Material.",
    icon: Disc,
  },
  {
    id: "auspuff",
    title: "Auspuff & Abgasanlage",
    discount: "10%",
    desc: "Auspuff brummt? Mit 10 % Materialrabatt klingen Sie wieder leise unterwegs.",
    icon: Wrench,
  },
  {
    id: "inspektion",
    title: "Inspektion",
    discount: "10%",
    desc: "10 % Materialnachlass auf Ihre nächste Inspektion. Termin morgens hinbringen, abends abholen.",
    icon: ClipboardCheck,
  },
  {
    id: "getriebe",
    title: "Automatik-Getriebespülung",
    discount: "10%",
    desc: "10 % Rabatt auf die professionelle Spülung nach der",
    descAfter: " — verlängert die Lebensdauer Ihres Getriebes deutlich.",
    icon: Sparkles,
    link: {
      url: "https://www.automatikoelwechselsystem.de",
      label: "Tim-Eckhard-Methode",
    },
  },
];

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return {
    year: d.getUTCFullYear(),
    week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7),
  };
}

function seededShuffle(arr, seed) {
  const out = [...arr];
  let s = seed * 9301 + 49297;
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Mulberry32 — kleiner deterministischer PRNG (gleicher Input → gleicher Output)
function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Liefert das Montagsdatum (UTC) einer ISO-Kalenderwoche
function isoWeekStartDate(year, week) {
  const d = new Date(Date.UTC(year, 0, 4));
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + (week - 1) * 7 - (dow - 1));
  return d;
}

/**
 * Wählt 2 Angebote für eine bestimmte Kalenderwoche.
 *
 * Garantien:
 *   1. Kein Angebot erscheint zwei Wochen hintereinander
 *      → Kandidatenliste schließt die Vorwoche immer aus
 *   2. Auf 3 Wochen verteilt erscheint jedes Angebot ≥ 1×
 *      → Round-Robin mit deterministischem Seed
 *   3. März–September: Klimaservice erhält ~20 % höhere Erscheinungsfrequenz
 *      ABER nur wenn er in der Vorwoche NICHT lief (Regel 1 hat Vorrang).
 *      Saison-Check erfolgt PRO Woche im Loop, damit Wochen 1–52 konsistent
 *      bleiben — egal von welcher Zielwoche aus simuliert wird.
 *
 * Die Funktion simuliert die Wochen vom Jahresbeginn bis zur Zielwoche, um
 * den „Vorwoche-Status" deterministisch zu erhalten — ohne Backend-Speicher.
 */
export function pickWeeklyOffers(date = new Date()) {
  const { year, week } = getISOWeek(date);

  let prevIds = [];
  let pair = [];

  for (let w = 1; w <= week; w++) {
    // Saison nach echtem Wochen-Datum, nicht nach Input-Datum
    const wStart = isoWeekStartDate(year, w);
    const wMonth = wStart.getUTCMonth() + 1;
    const inSeason = wMonth >= 3 && wMonth <= 9;

    const rand = mulberry32(year * 1000 + w);

    // Regel 1: Kandidaten = alles AUSSER Vorwoche
    const candidates = OFFERS.filter((o) => !prevIds.includes(o.id));

    // Regel 3: Saisonbias für Klimaservice — nur wenn er Kandidat ist
    const klima = candidates.find((o) => o.id === "klima");
    let first;
    if (inSeason && klima && rand() < 0.22) {
      // ~22 % bedingte Chance ergibt empirisch ~20 % Mehrfrequenz für
      // Klima zwischen März und September gegenüber Off-Season.
      first = klima;
    } else {
      first = candidates[Math.floor(rand() * candidates.length)];
    }

    const rest = candidates.filter((o) => o.id !== first.id);
    const second = rest[Math.floor(rand() * rest.length)];

    pair = [first, second];
    prevIds = pair.map((o) => o.id);
  }

  return pair;
}

export default function Offers() {
  const [ref, visible] = useReveal();
  const offers = useMemo(() => pickWeeklyOffers(new Date()), []);
  const { week, year } = useMemo(() => getISOWeek(new Date()), []);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((i) => (i + 1) % offers.length);
    }, 6500);
    return () => clearInterval(t);
  }, [offers.length]);

  const current = offers[active];
  const Icon = current?.icon || Tag;

  return (
    <section
      id="angebote"
      ref={ref}
      data-testid="offers-section"
      className={`relative bg-black border-t border-zinc-900 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="max-w-5xl mx-auto px-5 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase gold-text mb-4">
              — Aktuelle Angebote
            </div>
            <h2
              data-testid="offers-heading"
              className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.95]"
            >
              Diese Woche
              <br />
              <span className="gold-text">für Sie.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 border border-zinc-800 bg-[#0f0f12] px-4 py-3">
            <Calendar size={16} className="text-[#E0B33D]" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-300">
              KW {week} · {year}
            </div>
          </div>
        </div>

        {/* Eine große vertikale Tafel — Inhalt wechselt darin */}
        <div
          data-testid="offers-board"
          className="gold-border relative bg-[#0a0a0c] overflow-hidden mx-auto max-w-xl"
          style={{ minHeight: 760 }}
        >
          {/* Background atmosphere */}
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(224,179,61,0.18), transparent 55%), radial-gradient(circle at 85% 90%, rgba(255,215,94,0.12), transparent 60%)",
            }}
          />
          {/* Grain */}
          <div className="absolute inset-0 grain pointer-events-none" />

          {/* Header strip on board */}
          <div className="relative px-6 md:px-8 pt-7 md:pt-9 flex items-center justify-between">
            <div className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-zinc-500">
              Angebot {active + 1} / {offers.length}
            </div>
            <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase gold-text">
              Wöchentlich
            </div>
          </div>

          {/* Stacked offers — only the active one is visible (fade) */}
          <div className="relative px-6 md:px-8 pb-24 pt-12 md:pt-16" style={{ minHeight: 660 }}>
            {offers.map((offer, idx) => {
              const ActIcon = offer.icon || Tag;
              const isActive = idx === active;
              return (
                <article
                  key={offer.id}
                  data-testid={`offer-${offer.id}`}
                  aria-hidden={!isActive}
                  className={`absolute inset-x-6 md:inset-x-8 top-12 md:top-16 transition-all duration-700 ease-out ${
                    isActive
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 translate-y-4 pointer-events-none"
                  }`}
                >
                  <div className="flex flex-col gap-5">
                    <span
                      data-testid={`offer-icon-${offer.id}`}
                      className="w-16 h-16 md:w-20 md:h-20 grid place-items-center gold-bg"
                    >
                      <ActIcon size={30} strokeWidth={2.25} />
                    </span>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]">
                          {offer.title}
                        </h3>
                        {offer.seasonal && (
                          <span className="text-[10px] tracking-[0.3em] uppercase gold-text border border-[#E0B33D]/40 px-2 py-0.5">
                            Saison-Aktion
                          </span>
                        )}
                      </div>

                      <div className="mt-6 flex items-end gap-4">
                        <div className="font-display gold-text text-7xl md:text-8xl leading-none">
                          −{offer.discount}
                        </div>
                        <div className="pb-2 text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                          Rabatt
                        </div>
                      </div>

                      <p className="mt-6 text-[15px] text-zinc-300 leading-relaxed">
                        {offer.desc}
                        {offer.link && (
                          <>
                            {" "}
                            <a
                              href={offer.link.url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`offer-link-${offer.id}`}
                              className="gold-text underline decoration-[#E0B33D]/40 hover:decoration-[#FFD75E] underline-offset-4"
                            >
                              {offer.link.label}
                            </a>
                          </>
                        )}
                        {offer.descAfter}
                      </p>

                      <a
                        href="#termin"
                        data-testid={`offer-cta-${offer.id}`}
                        className="mt-8 inline-flex items-center gap-2 gold-bg text-black font-semibold px-6 py-3.5 text-[11px] tracking-[0.3em] uppercase"
                      >
                        Angebot sichern <ArrowUpRight size={14} />
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Bottom strip: indicators + auto-progress */}
          <div className="relative border-t border-zinc-800 bg-black/60 backdrop-blur px-6 md:px-8 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2" data-testid="offers-indicator">
              {offers.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Angebot ${i + 1} anzeigen`}
                  data-testid={`offer-dot-${i}`}
                  className={`h-1.5 transition-all duration-500 ${
                    i === active ? "w-12 gold-bg" : "w-4 bg-zinc-700 hover:bg-zinc-600"
                  }`}
                />
              ))}
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Auto-Wechsel
            </div>
          </div>
        </div>

        <p className="mt-6 text-[11px] text-zinc-500 max-w-2xl leading-relaxed">
          Die Angebote rotieren wöchentlich. Aktion gültig solange der Vorrat
          reicht und nicht mit anderen Rabatten kombinierbar.
        </p>
      </div>
    </section>
  );
}
